import axios from 'axios';

import logger from '@/shared/utils/logger';

const cacheExpiry = 1 * 60 * 60 * 1000; // 1 hour

export default async function getGravatarData(profileIdentifier) {
    try {
        if (!profileIdentifier) return;

        const cacheKey = `gravatarProfile-${profileIdentifier}`;
        const cachedProfile = localStorage.getItem(cacheKey);

        if (cachedProfile) {
            const { profile, timestamp } = JSON.parse(cachedProfile);
            if (Date.now() - timestamp < cacheExpiry) {
                return profile;
            }
        }

        const response = await axios.get(
            `https://api.gravatar.com/v3/profiles/${profileIdentifier}`
        );
        let { display_name, avatar_url, job_title, profile_url } =
            response.data;

        // Check if the avatar is the default image. Skip the probe entirely
        // when the profile has no avatar_url, otherwise we would issue a
        // request to the literal string "undefined?d=404".
        if (avatar_url) {
            const imgUrl = `${avatar_url}?d=404`;
            try {
                await axios.get(imgUrl);
                // If the request succeeds, there is a custom Gravatar image.
            } catch (error) {
                // A 404 means the image is the default one, so drop it.
                if (error.response && error.response.status === 404) {
                    avatar_url = undefined;
                } else {
                    logger.error('Error fetching the image:', error);
                }
            }
        }

        const profileData = {
            display_name,
            avatar_url,
            job_title,
            profile_url,
        };

        localStorage.setItem(
            cacheKey,
            JSON.stringify({ profile: profileData, timestamp: Date.now() })
        );

        return profileData;
    } catch (error) {
        // Gravatar enrichment is best-effort and consumed by Team.jsx through
        // optional chaining (data?.display_name). Return undefined on any
        // failure so the fallback shape matches the empty-identifier early
        // return above. Returning a truthy { error } object instead would make
        // a failed lookup look like a successful one to any consumer that
        // guards on `if (data)`.
        logger.error('Failed to fetch Gravatar profile:', error);
        return undefined;
    }
}
